import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Copy, Loader2, Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ProductForm } from "@/components/products/ProductForm";
import { ProductDeleteDialog } from "@/components/products/ProductDeleteDialog";
import { ProductDuplicateDialog } from "@/components/products/ProductDuplicateDialog";
import { InventoryProductPanel } from "@/components/inventory/InventoryProductPanel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { taxBadgeLabel } from "@/components/products/format";
import { useProduct } from "@/lib/api/products.queries";
import { useActiveCompany } from "@/lib/company-context";
import type { ProductDetail } from "@/lib/api/products";

export const Route = createFileRoute(
  "/companies/$companyId/productos/$productId",
)({
  component: ProductDetailPage,
});

function ProductDetailPage() {
  return (
    <RequireAuth>
      <AppShell>
        <ProductDetailContent />
      </AppShell>
    </RequireAuth>
  );
}

function ProductDetailContent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyId, productId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();
  const productQuery = useProduct(companyId, productId);
  const [editing, setEditing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  if (productQuery.isPending) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (productQuery.isError || !productQuery.data) {
    return (
      <div className="mx-auto max-w-3xl pt-6">
        <ErrorState
          title={t("products.detail.error.title")}
          description={t("products.detail.error.body")}
          onRetry={() => void productQuery.refetch()}
          retryLabel={t("common.retry")}
        />
      </div>
    );
  }

  const product = productQuery.data;

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6">
      <div className="flex flex-col gap-3">
        <Link
          to="/companies/$companyId/productos"
          params={{ companyId }}
          className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          {t("products.detail.back")}
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight">
                {product.name}
              </h1>
              <Badge className="text-[10px] uppercase">
                {t(`products.kind.${product.kind}`)}
              </Badge>
              {product.status === "archived" ? (
                <Badge variant="muted" className="text-[10px] uppercase">
                  {t("products.status.archived")}
                </Badge>
              ) : null}
            </div>
            {product.sku ? (
              <p className="mt-1 font-mono text-xs text-muted-foreground">
                SKU {product.sku}
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            {!editing ? (
              <Button variant="outline" onClick={() => setEditing(true)}>
                <Pencil className="h-4 w-4" />
                {t("products.edit")}
              </Button>
            ) : (
              <Button variant="outline" onClick={() => setEditing(false)}>
                {t("common.cancel")}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setDuplicateOpen(true)}
              disabled={editing}
            >
              <Copy className="h-4 w-4" />
              {t("products.duplicate")}
            </Button>
            <Button
              variant="outline"
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => setDeleteOpen(true)}
              disabled={product.status === "archived"}
            >
              <Trash2 className="h-4 w-4" />
              {t("products.delete")}
            </Button>
          </div>
        </div>
      </div>

      {editing ? (
        <ProductForm
          mode={{ kind: "edit", companyId, product }}
          onUpdated={() => setEditing(false)}
        />
      ) : product.kind === "product" ? (
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Detalles</TabsTrigger>
            <TabsTrigger value="inventario">Inventario</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-4">
            <OverviewCards product={product} />
          </TabsContent>
          <TabsContent value="inventario" className="mt-4">
            <InventoryProductPanel
              companyId={companyId}
              productId={product.id}
            />
          </TabsContent>
        </Tabs>
      ) : (
        <OverviewCards product={product} />
      )}

      <ProductDeleteDialog
        companyId={companyId}
        product={product}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onArchived={() =>
          void navigate({
            to: "/companies/$companyId/productos",
            params: { companyId },
          })
        }
      />

      <ProductDuplicateDialog
        companyId={companyId}
        product={product}
        open={duplicateOpen}
        onOpenChange={setDuplicateOpen}
      />
    </div>
  );
}

function OverviewCards({ product }: { product: ProductDetail }) {
  const formatCurrencyCop = useMoneyFormatter();
  const { t } = useTranslation();
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("products.detail.identity.title")}
          </CardTitle>
          <CardDescription>
            {t("products.detail.identity.subtitle")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-3">
            <Field label={t("products.form.kind")} value={t(`products.kind.${product.kind}`)} />
            <Field label={t("products.form.sku")} value={product.sku ?? "—"} />
            <Field
              label={t("products.form.standardCode")}
              value={
                product.standardCode
                  ? `${product.standardCode} (${product.standardCodeScheme})`
                  : "—"
              }
            />
            <Field
              label={t("products.form.brandName")}
              value={product.brandName ?? "—"}
            />
            <Field
              label={t("products.form.modelName")}
              value={product.modelName ?? "—"}
            />
            {product.description ? (
              <Field
                label={t("products.form.description")}
                value={product.description}
              />
            ) : null}
          </dl>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("products.detail.pricing.title")}
          </CardTitle>
          <CardDescription>
            {t("products.detail.pricing.subtitle")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-3">
            <Field
              label={t("products.form.unitPrice")}
              value={`${formatCurrencyCop(product.unitPrice)} / ${product.unitCode}`}
            />
            <Field
              label={t("products.form.priceIncludesTax")}
              value={product.priceIncludesTax ? t("common.yes") : t("common.no")}
            />
            <Field
              label={t("products.form.defaultTaxType")}
              value={taxBadgeLabel(product, t)}
            />
            <Field
              label={t("products.form.incomeAccount")}
              value={product.incomeAccountCode ?? "—"}
            />
            {product.notes ? (
              <Field
                label={t("products.form.notes")}
                value={product.notes}
              />
            ) : null}
          </dl>
        </CardContent>
      </Card>
      {product.components.length > 0 ? (
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">
              {t("products.components.title")}
            </CardTitle>
            <CardDescription>
              {t("products.components.subtitle")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="flex flex-col gap-2">
              {product.components.map((component) => (
                <li
                  key={component.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm"
                >
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <span className="font-medium">{component.label}</span>
                    <Badge variant="muted" className="text-[10px] uppercase">
                      {t(`products.components.kind.${component.kind}`)}
                    </Badge>
                    {component.incomeAccountCode ? (
                      <span className="font-mono text-xs text-muted-foreground">
                        {component.incomeAccountCode}
                      </span>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs">
                    <span className="font-mono font-semibold">
                      {component.basis === "percent"
                        ? `${component.value}%`
                        : formatCurrencyCop(component.value)}
                    </span>
                    {component.unitCost != null &&
                    component.costAccountCode ? (
                      <span className="text-muted-foreground">
                        {t("products.components.cost_summary", {
                          cost: formatCurrencyCop(component.unitCost),
                          account: component.costAccountCode,
                        })}
                      </span>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm">{value}</dd>
    </div>
  );
}
